import Vue from 'vue';

type ReactiveKey = string | number;
type ReactivePath = ReactiveKey | ReactiveKey[];
type ReactiveTarget = Record<ReactiveKey, any>;

function castArray(value: ReactivePath | undefined): ReactiveKey[] {
    return (Array.isArray(value) ? value : [value]).filter((key): key is ReactiveKey => key !== undefined);
}

function normalizePath(path: ReactivePath | undefined): ReactiveKey[] {
    const normalizedPath = castArray(path);

    if (normalizedPath.length) return normalizedPath;

    throw new Error('Reactive object path is required');
}

function isObject(value: unknown): value is ReactiveTarget {
    return value !== null && typeof value === 'object';
}

function ensureNestedTarget(target: ReactiveTarget, key: ReactiveKey): void {
    if (Object.hasOwn(target, key) && isObject(target[key])) return;

    Vue.set(target, key, {});
}

export function set<T>(target: ReactiveTarget, path: ReactivePath, value: T, merge = false): void {
    const [key, ...tail] = normalizePath(path);

    if (tail.length) {
        ensureNestedTarget(target, key);

        return set(target[key], tail, value, merge);
    }

    if (merge && isObject(value) && !Array.isArray(value)) {
        Vue.set(target, key, { ...target[key], ...value });
    } else {
        Vue.set(target, key, value);
    }
}

export function get<T = unknown>(target: unknown, path: ReactivePath | undefined): T | undefined {
    const [key, ...tail] = castArray(path);

    if (!isObject(target)) return;

    if (target[key] && tail.length) return get<T>(target[key], tail);

    return target[key] as T | undefined;
}

export function del(target: ReactiveTarget, path: ReactivePath): void {
    const [key, ...tail] = normalizePath(path);

    if (!Object.hasOwn(target, key)) return;

    if (tail.length) {
        if (!isObject(target[key])) return;

        return del(target[key], tail);
    }

    Vue.delete(target, key);
}
